import {
  TvmChains,
  StandaloneClientAdapter,
  TvmNetworkConfig,
  DEFAULT_NATIVE_CURRENCY_DECIMALS,
} from '@broxus/js-core';
import { paramsSerializer } from '@broxus/js-utils';
import {
  SparXWallet,
  TvmWalletProviderConfig,
  TvmWalletService,
  type RecentConnectionMeta,
  getRecentConnectionMeta,
} from '@broxus/tvm-connect/lib';
import { AddressLiteral, ProviderRpcClient } from 'everscale-inpage-provider';

import imgEverWallet from '@/assets/icons/EverWallet.svg';
import imgSparXWallet from '@/assets/icons/SparXWallet.svg';

type PredefinedConfig = {
  networkId?: number;
  providerId?: string;
  providers: TvmWalletProviderConfig[];
};

export const universalLink = [
  'https://l.sparxwallet.com',
  paramsSerializer({
    apn: 'com.broxus.sparx.app',
    ibi: 'app.sparx.broxus.com',
    isi: '6670219321',
    link: encodeURIComponent(window.location.href),
  }),
].join('?');

const sparxWallet: TvmWalletProviderConfig = {
  connector: new SparXWallet(),
  id: 'SparXWallet',
  info: {
    description: 'Your universal tool for TVM',
    icon: imgSparXWallet,
    links: {
      android:
        'https://play.google.com/store/apps/details?id=com.broxus.sparx.app',
      chromeExtension:
        'https://chromewebstore.google.com/detail/sparx-wallet/aijecocmefcagpmbpjcfjjbcclfmobgf',
      homepage: 'https://sparxwallet.com/',
      ios: 'https://apps.apple.com/us/app/sparx-tvm-wallet/id6670219321',
      universalLink,
    },
    name: 'SparX Wallet',
  },
};

function getPredefinedConfig(
  meta?: RecentConnectionMeta | undefined,
): PredefinedConfig {
  const providers: TvmWalletProviderConfig[] = [];

  let providerId = meta?.providerId ?? sparxWallet.id,
    networkId = meta?.chainId
      ? Number(meta.chainId)
      : TvmChains.EverscaleMainnet;

  providers.push(sparxWallet);
  providerId = sparxWallet.id;
  networkId = TvmChains.EverscaleMainnet;

  return { networkId, providerId, providers };
}

export const network: TvmNetworkConfig = {
  chainId: TvmChains.EverscaleMainnet.toString(),
  currency: {
    decimals: DEFAULT_NATIVE_CURRENCY_DECIMALS,
    icon: imgEverWallet,
    name: 'EVER',
    symbol: 'EVER',
    wrappedCurrencyAddress: new AddressLiteral(
      '0:a49cd4e158a9a15555e624759e2e4e766d22600b7800d891e46f9291f044a93d',
    ),
  },
  explorer: {
    accountsSubPath: 'accounts',
    baseUrl: 'https://everscan.io',
    title: 'Everscale Explorer',
    transactionsSubPath: 'transactions',
  },
  icon: imgEverWallet,
  id: `tvm-${TvmChains.EverscaleMainnet}`,
  name: 'Everscale',
  rpcUrl: 'https://jrpc.everwallet.net/',
  shortName: 'Everscale',
  type: 'tvm',
};

export function createTvmWallet(): TvmWalletService {
  const recentMeta = getRecentConnectionMeta();
  const predefinedConfig = getPredefinedConfig(recentMeta);

  return new TvmWalletService({
    connectionParams: {
      factory: (network) =>
        new ProviderRpcClient({
          provider: new StandaloneClientAdapter({
            connection: network
              ? network.connectionProperties || {
                  data: {
                    endpoint: network.rpcUrl,
                  },
                  id: Number(network.chainId),
                  type: 'proto', // isProduction ? 'proto' : 'jrpc',
                }
              : 'mainnetJrpc',
          }),
        }),
    },
    defaultNetworkId: predefinedConfig.networkId,
    networks: [network],
    providerId: predefinedConfig.providerId,
    providers: predefinedConfig.providers,
  });
}

export const tvmWalletService = createTvmWallet();
